import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Users as UsersIcon, Search, Filter, ChevronLeft, ChevronRight, User, Power, X, UserCheck, UserX, AlertTriangle, Shield, Lock, Check, Globe, ChevronDown, ChevronUp, Save, Settings as SettingsIcon, ToggleRight } from 'lucide-react';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import { apiService, isAdmin } from '../services/api';
import ProcessingStatus, { dimClass } from '../components/ProcessingStatus';
import { Skel, SkelRows } from '../components/Skeleton';
import BusyIcon from '../components/BusyIcon';

// ============================================================================
// User List Tab
// ============================================================================
const UserListTab = ({ userIsAdmin }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statistics, setStatistics] = useState({});
  const [pagination, setPagination] = useState({});
  const [filters, setFilters] = useState({ email: '', name: '', status: 'active', page: 1, page_size: 50 });
  const [togglingUser, setTogglingUser] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, user: null });

  useEffect(() => { fetchUsers(); }, [filters.page, filters.page_size]);

  const fetchUsers = async (sf = null) => {
    try {
      setLoading(true); setError(null);
      const f = sf || filters;
      const params = new URLSearchParams();
      params.append('page', f.page); params.append('page_size', f.page_size);
      if (f.email) params.append('email', f.email);
      if (f.name) params.append('name', f.name);
      if (f.status) params.append('status', f.status);
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_BASE_URL}/analytics/users-list/?${params}`, { headers: { 'Authorization': `Basic ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setUsers(data.users); setPagination(data.pagination); setStatistics(data.statistics);
    } catch (e) { setError('Failed to load users.'); } finally { setLoading(false); }
  };

  const handleSearch = () => { const f = { ...filters, page: 1 }; setFilters(f); fetchUsers(f); };
  const handleClear = () => { const f = { email: '', name: '', status: '', page: 1, page_size: filters.page_size }; setFilters(f); fetchUsers(f); };
  const hasActiveFilters = filters.email || filters.name;

  const handleConfirmToggle = async () => {
    const user = confirmDialog.user; if (!user) return;
    setConfirmDialog({ open: false, user: null }); setTogglingUser(user.id);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_BASE_URL}/analytics/users/${user.id}/toggle-status/`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Basic ${token}` } });
      if (!res.ok) throw new Error();
      const data = await res.json();
      if (data.success) {
        const remove = (filters.status === 'active' && !data.is_active) || (filters.status === 'inactive' && data.is_active);
        if (remove) setUsers(users.filter(u => u.id !== user.id));
        else setUsers(users.map(u => u.id === user.id ? { ...u, is_active: data.is_active } : u));
        setStatistics(p => ({ ...p, active_users: data.is_active ? p.active_users + 1 : p.active_users - 1, inactive_users: data.is_active ? p.inactive_users - 1 : p.inactive_users + 1 }));
      }
    } catch {} finally { setTogglingUser(null); }
  };

  const formatDate = (d) => { if (!d) return 'Never'; return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); };
  const getStatusBadge = (u) => {
    if (!u.is_active) return <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded-full border border-red-500/20">Inactive</span>;
    if (u.is_superuser) return <span className="px-2 py-1 bg-purple-500/10 text-purple-400 text-xs rounded-full border border-purple-500/20">Superuser</span>;
    if (u.is_staff) return <span className="px-2 py-1 bg-yellow-500/10 text-yellow-400 text-xs rounded-full border border-yellow-500/20">Staff</span>;
    return <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded-full border border-green-500/20">Active</span>;
  };

  if (error) return <div className="flex items-center justify-center h-64"><div className="text-center"><p className="text-red-400 mb-2">{error}</p><button onClick={() => fetchUsers()} className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">Retry</button></div></div>;

  return (
    <div className="space-y-6">
      <div className="dashboard-card">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-1 bg-dark-900 p-1 rounded-lg">
            {[{ label: 'All Users', val: '' }, { label: 'Active', val: 'active', icon: UserCheck }, { label: 'Inactive', val: 'inactive', icon: UserX }].map(({ label, val, icon: Icon }) => (
              <button key={val} onClick={() => { const f = { ...filters, status: val, page: 1 }; setFilters(f); fetchUsers(f); }}
                className={`px-5 py-2.5 rounded-md text-sm font-medium transition-all flex items-center gap-1.5 ${filters.status === val ? (val === 'active' ? 'bg-green-600' : val === 'inactive' ? 'bg-red-600' : 'bg-blue-600') + ' text-white shadow-lg' : 'text-dark-300 hover:text-white hover:bg-dark-700'}`}>
                {Icon && <Icon className="w-4 h-4" />}{label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-6">
            {[{ icon: UsersIcon, count: statistics.total_users, label: 'Total', color: 'blue' }, { icon: UserCheck, count: statistics.active_users, label: 'Active', color: 'green' }, { icon: UserX, count: statistics.inactive_users, label: 'Inactive', color: 'red' }].map(({ icon: I, count, label, color }) => (
              <div key={label} className="flex items-center gap-2"><I className={`w-5 h-5 text-${color}-400`} />{count == null && loading ? <Skel className="h-4 w-8" /> : <span className="text-white font-semibold">{count?.toLocaleString() || 0}</span>}<span className="text-dark-400 text-sm">{label}</span></div>
            ))}
          </div>
        </div>
      </div>

      {hasActiveFilters && (
        <div className="flex items-center gap-2">
          <span className="text-dark-400 text-sm">Active filters:</span>
          {filters.email && <span className="px-3 py-1 bg-blue-500/20 text-blue-400 text-sm rounded-full flex items-center gap-1">Email: {filters.email}<X className="w-3 h-3 cursor-pointer" onClick={() => { const f = { ...filters, email: '', page: 1 }; setFilters(f); fetchUsers(f); }} /></span>}
          {filters.name && <span className="px-3 py-1 bg-blue-500/20 text-blue-400 text-sm rounded-full flex items-center gap-1">Name: {filters.name}<X className="w-3 h-3 cursor-pointer" onClick={() => { const f = { ...filters, name: '', page: 1 }; setFilters(f); fetchUsers(f); }} /></span>}
          <button onClick={handleClear} className="px-3 py-1 bg-dark-700 text-dark-300 text-sm rounded-full hover:bg-dark-600 flex items-center gap-1"><X className="w-3 h-3" />Clear All</button>
        </div>
      )}

      <div className="dashboard-card">
        <div className="space-y-4">
          <div className="flex items-center space-x-2 mb-4"><Filter className="w-5 h-5 text-gray-400" /><h3 className="text-lg font-semibold text-white">Filters & Search</h3></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><label className="block text-sm font-medium text-dark-300 mb-2">Email / Username</label><div className="relative"><Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-dark-400" /><input type="text" placeholder="Search..." value={filters.email} onChange={(e) => setFilters({ ...filters, email: e.target.value })} onKeyPress={(e) => e.key === 'Enter' && handleSearch()} className="w-full pl-10 pr-4 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:border-primary-500" /></div></div>
            <div><label className="block text-sm font-medium text-dark-300 mb-2">Name</label><div className="relative"><User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-dark-400" /><input type="text" placeholder="Search by name..." value={filters.name} onChange={(e) => setFilters({ ...filters, name: e.target.value })} onKeyPress={(e) => e.key === 'Enter' && handleSearch()} className="w-full pl-10 pr-4 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:border-primary-500" /></div></div>
            <div><label className="block text-sm font-medium text-dark-300 mb-2">Page Size</label><select value={filters.page_size} onChange={(e) => { const f = { ...filters, page_size: parseInt(e.target.value), page: 1 }; setFilters(f); fetchUsers(f); }} className="w-full px-4 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500"><option value={25}>25</option><option value={50}>50</option><option value={100}>100</option><option value={250}>250</option></select></div>
          </div>
          <div className="flex space-x-3">
            <button onClick={handleSearch} className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center space-x-2"><Search className="w-4 h-4" /><span>Search</span></button>
            <button onClick={handleClear} className="px-6 py-2 bg-dark-700 text-white rounded-lg hover:bg-dark-600">Clear Filters</button>
          </div>
        </div>
      </div>

      <div className="dashboard-card">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-white">Users {pagination.total_count == null && loading ? <Skel className="h-4 w-10" testId="users-count-pending" /> : `(${pagination.total_count?.toLocaleString() || 0})`}</h3>
          {pagination.total_count > 0 && <div className="text-sm text-dark-400">Showing {((pagination.page - 1) * pagination.page_size) + 1} to {Math.min(pagination.page * pagination.page_size, pagination.total_count)} of {pagination.total_count}</div>}
        </div>
        {loading && !users.length ? (
          <div data-testid="users-skeleton">
            <ProcessingStatus active title="Loading users" />
            <table className="w-full"><thead><tr className="border-b border-dark-700">
              {['User', 'Email', 'Status', 'Last Login', ...(userIsAdmin ? ['Actions'] : [])].map(c => <th key={c} className="text-left py-3 px-4 text-dark-300 font-medium">{c}</th>)}
            </tr></thead><tbody><SkelRows cols={userIsAdmin ? 5 : 4} rows={8} cellClass="py-5 px-4" /></tbody></table>
          </div>
        )
        : users.length === 0 ? <div className="text-center py-12"><UsersIcon className="w-12 h-12 text-dark-500 mx-auto mb-4" /><p className="text-dark-300">No users found</p></div>
        : <><ProcessingStatus active={loading} title="Updating users" /><div className={`overflow-x-auto ${dimClass(loading)}`}><table className="w-full"><thead><tr className="border-b border-dark-700"><th className="text-left py-3 px-4 text-dark-300 font-medium">User</th><th className="text-left py-3 px-4 text-dark-300 font-medium">Email</th><th className="text-left py-3 px-4 text-dark-300 font-medium">Status</th><th className="text-left py-3 px-4 text-dark-300 font-medium">Last Login</th>{userIsAdmin && <th className="text-left py-3 px-4 text-dark-300 font-medium">Actions</th>}</tr></thead>
          <tbody>{users.map((user) => { const prot = user.username === 'apalshah' && user.is_active; return (
            <tr key={user.id} className="border-b border-dark-800 hover:bg-dark-800/50">
              <td className="py-4 px-4"><div className="flex items-center space-x-3"><div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">{user.first_name ? user.first_name[0].toUpperCase() : user.username[0].toUpperCase()}</div><div><p className="text-white font-medium flex items-center gap-2">{user.first_name || user.last_name ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : user.username}{prot && <Shield className="w-3.5 h-3.5 text-amber-400" title="Protected" />}</p><p className="text-dark-400 text-sm">@{user.username} &middot; ID: {user.id}</p></div></div></td>
              <td className="py-4 px-4"><span className="text-dark-200">{user.email || 'No email'}</span></td>
              <td className="py-4 px-4">{getStatusBadge(user)}</td>
              <td className="py-4 px-4"><span className="text-dark-200 text-sm">{formatDate(user.last_login)}</span></td>
              {userIsAdmin && <td className="py-4 px-4">{prot ? <span className="text-dark-500 text-xs">Protected</span> : <button onClick={() => setConfirmDialog({ open: true, user })} disabled={togglingUser === user.id} aria-busy={togglingUser === user.id} data-testid={`user-toggle-${user.id}`} className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors ${user.is_active ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20' : 'bg-green-500/10 text-green-400 hover:bg-green-500/20 border border-green-500/20'} ${togglingUser === user.id ? 'opacity-50' : ''}`}><BusyIcon busy={togglingUser === user.id} icon={Power} />{togglingUser === user.id ? (user.is_active ? 'Deactivating…' : 'Activating…') : (user.is_active ? 'Deactivate' : 'Activate')}</button>}</td>}
            </tr>); })}</tbody></table></div></>}

        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-6 border-t border-dark-700">
            <div className="text-sm text-dark-400">Page {pagination.page} of {pagination.total_pages}</div>
            <div className="flex items-center space-x-2">
              <button onClick={() => { const f = { ...filters, page: 1 }; setFilters(f); fetchUsers(f); }} disabled={!pagination.has_previous} className="px-3 py-2 bg-dark-700 text-white rounded-lg hover:bg-dark-600 disabled:opacity-50 text-sm">First</button>
              <button onClick={() => { const f = { ...filters, page: filters.page - 1 }; setFilters(f); fetchUsers(f); }} disabled={!pagination.has_previous} className="p-2 bg-dark-700 text-white rounded-lg hover:bg-dark-600 disabled:opacity-50"><ChevronLeft className="w-4 h-4" /></button>
              <span className="px-4 py-2 bg-primary-600 text-white rounded-lg">{pagination.page}</span>
              <button onClick={() => { const f = { ...filters, page: filters.page + 1 }; setFilters(f); fetchUsers(f); }} disabled={!pagination.has_next} className="p-2 bg-dark-700 text-white rounded-lg hover:bg-dark-600 disabled:opacity-50"><ChevronRight className="w-4 h-4" /></button>
              <button onClick={() => { const f = { ...filters, page: pagination.total_pages }; setFilters(f); fetchUsers(f); }} disabled={!pagination.has_next} className="px-3 py-2 bg-dark-700 text-white rounded-lg hover:bg-dark-600 disabled:opacity-50 text-sm">Last</button>
            </div>
          </div>
        )}
      </div>

      {confirmDialog.open && confirmDialog.user && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-dark-800 rounded-xl p-6 max-w-md w-full mx-4 border border-dark-600 shadow-xl">
            <div className="flex items-center gap-3 mb-4"><div className={`w-12 h-12 rounded-full flex items-center justify-center ${confirmDialog.user.is_active ? 'bg-red-500/20' : 'bg-green-500/20'}`}><AlertTriangle className={`w-6 h-6 ${confirmDialog.user.is_active ? 'text-red-400' : 'text-green-400'}`} /></div><div><h3 className="text-lg font-semibold text-white">{confirmDialog.user.is_active ? 'Deactivate User?' : 'Activate User?'}</h3><p className="text-dark-400 text-sm">This action can be reversed</p></div></div>
            <div className="bg-dark-700/50 rounded-lg p-4 mb-6"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">{confirmDialog.user.first_name ? confirmDialog.user.first_name[0].toUpperCase() : confirmDialog.user.username[0].toUpperCase()}</div><div><p className="text-white font-medium">{confirmDialog.user.first_name || confirmDialog.user.last_name ? `${confirmDialog.user.first_name || ''} ${confirmDialog.user.last_name || ''}`.trim() : confirmDialog.user.username}</p><p className="text-dark-400 text-sm">{confirmDialog.user.email || `@${confirmDialog.user.username}`}</p></div></div></div>
            <p className="text-dark-300 text-sm mb-6">{confirmDialog.user.is_active ? 'This user will no longer be able to access the platform.' : 'This user will regain access.'}</p>
            <div className="flex gap-3"><button onClick={() => setConfirmDialog({ open: false, user: null })} className="flex-1 px-4 py-2.5 bg-dark-700 text-white rounded-lg hover:bg-dark-600 font-medium">Cancel</button><button onClick={handleConfirmToggle} className={`flex-1 px-4 py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 ${confirmDialog.user.is_active ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-green-600 text-white hover:bg-green-700'}`}><Power className="w-4 h-4" />{confirmDialog.user.is_active ? 'Deactivate' : 'Activate'}</button></div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Index Management Tab (Symbol activation/deactivation)
// ============================================================================
const IndexManagementTab = ({ userIsAdmin }) => {
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedMarkets, setExpandedMarkets] = useState({});
  const [togglingSymbol, setTogglingSymbol] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, symbol: null });
  const [statistics, setStatistics] = useState({ total: 0, active: 0, inactive: 0 });

  useEffect(() => { fetchSymbols(); }, []);

  const fetchSymbols = async () => {
    try {
      setLoading(true);
      const res = await apiService.getSymbols();
      const symbolsList = res.data || [];
      const active = symbolsList.filter(s => s.is_active).length;
      setStatistics({ total: symbolsList.length, active, inactive: symbolsList.length - active });

      const groups = {};
      symbolsList.forEach(s => {
        const m = s.market_name || 'unknown';
        if (!groups[m]) groups[m] = { name: m, symbols: [] };
        groups[m].symbols.push(s);
      });

      const order = ['india_stocks_indexes', 'us_stocks_indexes', 'international_stocks_indexes', 'precious_metals', 'crypto'];
      const sorted = Object.values(groups).sort((a, b) => {
        const ia = order.indexOf(a.name), ib = order.indexOf(b.name);
        return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
      });
      setMarkets(sorted);

      const exp = {};
      Object.keys(groups).forEach(m => { exp[m] = true; });
      setExpandedMarkets(exp);
    } catch { setError('Failed to load symbols'); } finally { setLoading(false); }
  };

  const toggleSymbol = async (symbol) => {
    try {
      setTogglingSymbol(symbol.id);
      setConfirmDialog({ open: false, symbol: null });
      const res = await apiService.toggleSymbolStatus(symbol.id);
      const result = res.data;
      setMarkets(prev => prev.map(m => ({ ...m, symbols: m.symbols.map(s => s.id === symbol.id ? { ...s, is_active: result.is_active } : s) })));
      setStatistics(prev => ({ ...prev, active: prev.active + (result.is_active ? 1 : -1), inactive: prev.inactive + (result.is_active ? -1 : 1) }));
    } catch { setError('Failed to update symbol'); } finally { setTogglingSymbol(null); }
  };

  const formatName = (n) => n.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  if (loading) return (
    <div className="space-y-4" data-testid="symbols-skeleton">
      <ProcessingStatus active title="Loading symbols" className="" />
      <div className="grid grid-cols-3 gap-4">
        {['Total Symbols', 'Active', 'Inactive'].map(l => (
          <div key={l} className="bg-dark-800 rounded-lg p-4 border border-dark-700">
            <div className="h-8 flex items-center"><Skel className="h-6 w-10" /></div>
            <div className="text-sm text-dark-400">{l}</div>
          </div>
        ))}
      </div>
      {[0, 1, 2, 3].map(i => (
        <div key={i} className="bg-dark-800 rounded-lg border border-dark-700 p-4 flex items-center gap-3">
          <Skel className="h-5 w-5" /><Skel className="h-4 w-40" /><Skel className="h-3 w-24 ml-auto" />
        </div>
      ))}
    </div>
  );
  if (error) return <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400">{error}</div>;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-dark-800 rounded-lg p-4 border border-dark-700"><div className="text-2xl font-bold text-white">{statistics.total}</div><div className="text-sm text-dark-400">Total Symbols</div></div>
        <div className="bg-dark-800 rounded-lg p-4 border border-green-500/30"><div className="text-2xl font-bold text-green-400">{statistics.active}</div><div className="text-sm text-dark-400">Active</div></div>
        <div className="bg-dark-800 rounded-lg p-4 border border-red-500/30"><div className="text-2xl font-bold text-red-400">{statistics.inactive}</div><div className="text-sm text-dark-400">Inactive</div></div>
      </div>

      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-start space-x-2">
        <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-amber-300">Deactivating a symbol hides it globally — from the dashboard, reports, and all users. Only active symbols are visible across the platform.</div>
      </div>

      {markets.map(market => {
        const activeCount = market.symbols.filter(s => s.is_active).length;
        const isExpanded = expandedMarkets[market.name];
        return (
          <div key={market.name} className="bg-dark-800 rounded-lg border border-dark-700 overflow-hidden">
            <button onClick={() => setExpandedMarkets(p => ({ ...p, [market.name]: !p[market.name] }))} className="w-full flex items-center justify-between p-4 hover:bg-dark-700/50 transition-colors">
              <div className="flex items-center space-x-3">
                {isExpanded ? <ChevronDown className="w-5 h-5 text-dark-400" /> : <ChevronUp className="w-5 h-5 text-dark-400" />}
                <Globe className="w-5 h-5 text-blue-400" />
                <span className="text-white font-medium">{formatName(market.name)}</span>
              </div>
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${activeCount === market.symbols.length ? 'bg-green-500/20 text-green-400' : activeCount === 0 ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}`}>{activeCount}/{market.symbols.length} active</span>
            </button>
            {isExpanded && (
              <div className="px-4 pb-4"><div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {market.symbols.map(symbol => (
                  <div key={symbol.id} className={`rounded-lg p-3 border transition-all ${symbol.is_active ? 'bg-dark-700/50 border-green-500/30' : 'bg-dark-900/50 border-dark-600 opacity-60'}`}>
                    <div className="flex items-center justify-between">
                      <div className="min-w-0 flex-1"><div className={`font-medium text-sm ${symbol.is_active ? 'text-white' : 'text-dark-400'}`}>{symbol.name}</div><div className="text-xs text-dark-500 truncate">{symbol.label}</div></div>
                      {userIsAdmin && togglingSymbol === symbol.id && <BusyIcon busy className="w-3.5 h-3.5 text-primary-400 ml-2" />}
                      {userIsAdmin && (
                        <button onClick={() => symbol.is_active ? setConfirmDialog({ open: true, symbol }) : toggleSymbol(symbol)} disabled={togglingSymbol === symbol.id} aria-busy={togglingSymbol === symbol.id} data-testid={`symbol-toggle-${symbol.id}`}
                          className={`ml-2 relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${togglingSymbol === symbol.id ? 'opacity-50' : ''} ${symbol.is_active ? 'bg-green-500' : 'bg-dark-600'}`}>
                          <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${symbol.is_active ? 'translate-x-5' : 'translate-x-0'}`} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div></div>
            )}
          </div>
        );
      })}

      {confirmDialog.open && confirmDialog.symbol && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-dark-800 rounded-xl border border-dark-700 p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center space-x-3 mb-4"><div className="p-2 rounded-full bg-red-500/20"><AlertTriangle className="w-6 h-6 text-red-400" /></div><h3 className="text-lg font-semibold text-white">Deactivate Symbol</h3></div>
            <p className="text-dark-300 mb-2">Are you sure you want to deactivate <span className="text-white font-medium">{confirmDialog.symbol.name}</span>?</p>
            <p className="text-sm text-dark-400 mb-6">This will hide {confirmDialog.symbol.name} ({confirmDialog.symbol.label}) from the dashboard, reports, and all users globally.</p>
            <div className="flex justify-end space-x-3">
              <button onClick={() => setConfirmDialog({ open: false, symbol: null })} className="px-4 py-2 rounded-lg bg-dark-700 text-dark-300 hover:bg-dark-600">Cancel</button>
              <button onClick={() => toggleSymbol(confirmDialog.symbol)} className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-500">Deactivate</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Index Permissions Tab (per-user symbol access)
// ============================================================================
const IndexPermissionsTab = ({ userIsAdmin }) => {
  const [users, setUsers] = useState([]);
  const [symbols, setSymbols] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [originalPermissions, setOriginalPermissions] = useState({});
  const [shortsAllowed, setShortsAllowed] = useState({});
  const [originalShortsAllowed, setOriginalShortsAllowed] = useState({});
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedMarkets, setExpandedMarkets] = useState({});
  const [confirmDialog, setConfirmDialog] = useState({ open: false });
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => { fetchInitialData(); }, []);
  useEffect(() => { if (selectedUserId) fetchUserPermissions(selectedUserId); }, [selectedUserId]);
  useEffect(() => {
    if (selectedUserId) setHasChanges(
      JSON.stringify(permissions) !== JSON.stringify(originalPermissions) ||
      JSON.stringify(shortsAllowed) !== JSON.stringify(originalShortsAllowed)
    );
  }, [permissions, originalPermissions, shortsAllowed, originalShortsAllowed, selectedUserId]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [usersRes, symbolsRes] = await Promise.all([
        apiService.getUsersList({ page_size: 1000, status: 'active' }),
        apiService.getSymbols(),
      ]);
      const activeUsers = (usersRes.data.users || []).filter(u => u.is_active);
      setUsers(activeUsers);
      const symbolsList = symbolsRes.data || [];
      setSymbols(symbolsList);

      const groups = {};
      symbolsList.forEach(s => {
        const m = s.market_name || 'unknown';
        if (!groups[m]) groups[m] = { id: s.market, name: m, symbols: [] };
        groups[m].symbols.push(s);
      });
      const order = ['india_stocks_indexes', 'us_stocks_indexes', 'international_stocks_indexes', 'precious_metals', 'crypto'];
      setMarkets(Object.values(groups).sort((a, b) => (order.indexOf(a.name) === -1 ? 999 : order.indexOf(a.name)) - (order.indexOf(b.name) === -1 ? 999 : order.indexOf(b.name))));
      const exp = {}; Object.keys(groups).forEach(m => { exp[m] = true; }); setExpandedMarkets(exp);
    } catch { } finally { setLoading(false); }
  };

  const fetchUserPermissions = async (userId) => {
    try {
      const res = await apiService.getUserPermissions(userId);
      const permMap = {};
      const shortsMap = {};
      const perms = res.data.permissions || [];
      perms.forEach(p => {
        const symId = p.symbol_id ?? p.symbol;
        if (symId != null) {
          permMap[symId] = true;
          if (p.shorts_allowed) shortsMap[symId] = true;
        }
      });
      setPermissions(permMap); setOriginalPermissions(permMap);
      setShortsAllowed(shortsMap); setOriginalShortsAllowed(shortsMap);
    } catch { setPermissions({}); setOriginalPermissions({}); setShortsAllowed({}); setOriginalShortsAllowed({}); }
  };

  const toggleSymbol = (symbolId) => {
    if (!userIsAdmin) return;
    setPermissions(p => ({ ...p, [symbolId]: !p[symbolId] }));
    // Revoke shorts if revoking access
    if (permissions[symbolId]) setShortsAllowed(s => { const n = { ...s }; delete n[symbolId]; return n; });
  };
  const toggleSymbolShort = (symbolId) => {
    if (!userIsAdmin || !permissions[symbolId]) return;
    setShortsAllowed(s => ({ ...s, [symbolId]: !s[symbolId] }));
  };
  const toggleMarket = (syms, grant) => {
    if (!userIsAdmin) return;
    setPermissions(p => { const u = { ...p }; syms.forEach(s => { u[s.id] = grant; }); return u; });
    if (!grant) setShortsAllowed(s => { const n = { ...s }; syms.forEach(sym => { delete n[sym.id]; }); return n; });
  };

  const confirmSave = async () => {
    setConfirmDialog({ open: false }); setSaving(true);
    try {
      const ids = Object.entries(permissions).filter(([_, g]) => g).map(([id]) => parseInt(id));
      const shortsIds = Object.entries(shortsAllowed).filter(([_, g]) => g).map(([id]) => parseInt(id));
      await apiService.setUserPermissions(selectedUserId, ids, shortsIds);
      setOriginalPermissions({ ...permissions }); setOriginalShortsAllowed({ ...shortsAllowed }); setHasChanges(false);
    } catch { } finally { setSaving(false); }
  };

  const selectedUser = users.find(u => u.id === selectedUserId);

  if (loading) return (
    <div className="dashboard-card" data-testid="permissions-skeleton">
      <div className="flex items-center space-x-2 mb-4"><Lock className="w-5 h-5 text-orange-400" /><h3 className="text-lg font-semibold text-white">Index Access Permissions</h3></div>
      <ProcessingStatus active title="Loading index permissions" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><span className="block text-sm font-medium text-dark-300 mb-2">Select User</span><Skel className="h-[50px] w-full" rounded="rounded-lg" /></div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="dashboard-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2"><Lock className="w-5 h-5 text-orange-400" /><h3 className="text-lg font-semibold text-white">Index Access Permissions</h3></div>
          {hasChanges && <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 text-sm rounded-full">Unsaved changes</span>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="block text-sm font-medium text-dark-300 mb-2">Select User</label>
            <select value={selectedUserId || ''} onChange={(e) => setSelectedUserId(e.target.value ? parseInt(e.target.value) : null)} className="w-full px-4 py-3 bg-dark-800 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500">
              <option value="">-- Select a user --</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.email || u.username} {u.first_name ? `(${u.first_name} ${u.last_name || ''})` : ''}</option>)}
            </select>
          </div>
          {selectedUser && (
            <div className="flex items-center space-x-3 p-3 bg-dark-700/50 rounded-lg">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold">{selectedUser.first_name ? selectedUser.first_name[0].toUpperCase() : selectedUser.username[0].toUpperCase()}</div>
              <div><p className="text-white font-medium">{selectedUser.first_name ? `${selectedUser.first_name} ${selectedUser.last_name || ''}` : selectedUser.username}</p><p className="text-dark-400 text-sm">{selectedUser.email || `@${selectedUser.username}`}</p></div>
            </div>
          )}
        </div>
      </div>

      {selectedUserId && (
        <div className="dashboard-card">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-white">Symbol Permissions</h3>
            <span className="text-dark-400 text-sm">{Object.values(permissions).filter(Boolean).length} of {symbols.length} symbols</span>
          </div>
          <div className="space-y-4">
            {markets.map(market => {
              const stats = { total: market.symbols.length, granted: market.symbols.filter(s => permissions[s.id]).length };
              const isExpanded = expandedMarkets[market.name];
              return (
                <div key={market.name} className="border border-dark-600 rounded-lg overflow-hidden">
                  <div className="flex items-center justify-between p-4 bg-dark-700/50 cursor-pointer hover:bg-dark-700" onClick={() => setExpandedMarkets(p => ({ ...p, [market.name]: !p[market.name] }))}>
                    <div className="flex items-center space-x-3">
                      {isExpanded ? <ChevronUp className="w-5 h-5 text-dark-400" /> : <ChevronDown className="w-5 h-5 text-dark-400" />}
                      <Globe className="w-5 h-5 text-blue-400" />
                      <span className="text-white font-medium capitalize">{market.name.replace(/_/g, ' ')}</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${stats.granted === stats.total ? 'bg-green-500/20 text-green-400' : stats.granted === 0 ? 'bg-dark-600 text-dark-400' : 'bg-yellow-500/20 text-yellow-400'}`}>{stats.granted}/{stats.total}</span>
                    </div>
                    {userIsAdmin && (
                      <div className="flex items-center space-x-2" onClick={e => e.stopPropagation()}>
                        <button onClick={() => toggleMarket(market.symbols, true)} className="px-3 py-1 text-xs font-medium bg-green-500/20 text-green-400 rounded hover:bg-green-500/30">Grant All</button>
                        <button onClick={() => toggleMarket(market.symbols, false)} className="px-3 py-1 text-xs font-medium bg-red-500/20 text-red-400 rounded hover:bg-red-500/30">Revoke All</button>
                      </div>
                    )}
                  </div>
                  {isExpanded && (
                    <div className="p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {market.symbols.map(symbol => {
                        const isGranted = permissions[symbol.id];
                        const shortsOn = shortsAllowed[symbol.id];
                        return (
                          <div key={symbol.id} className={`flex items-center p-3 rounded-lg border transition-all ${isGranted ? 'bg-green-500/10 border-green-500/30' : 'bg-dark-800 border-dark-600'} ${!userIsAdmin ? 'opacity-75' : ''}`}>
                            <div onClick={() => toggleSymbol(symbol.id)} className="flex items-center space-x-3 flex-1 min-w-0 cursor-pointer">
                              <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${isGranted ? 'bg-green-500' : 'bg-dark-600'}`}>{isGranted && <Check className="w-3 h-3 text-white" />}</div>
                              <div className="flex-1 min-w-0"><p className={`font-medium truncate ${isGranted ? 'text-green-400' : 'text-white'}`}>{symbol.name}</p><p className="text-dark-400 text-xs truncate">{symbol.label}</p></div>
                            </div>
                            <div className="flex items-center gap-1.5 flex-shrink-0 ml-2" onClick={e => e.stopPropagation()}>
                              <span className={`text-[10px] font-semibold uppercase tracking-wide ${shortsOn ? 'text-orange-300' : 'text-dark-500'} ${!isGranted ? 'opacity-40' : ''}`}>Shorts</span>
                              <button type="button" role="switch" aria-checked={!!shortsOn}
                                disabled={!userIsAdmin || !isGranted}
                                onClick={() => toggleSymbolShort(symbol.id)}
                                title={!isGranted ? 'Grant access first' : shortsOn ? 'Shorts allowed — click to revoke' : 'Allow shorts for this symbol'}
                                className={`relative w-8 h-[18px] rounded-full transition-colors flex-shrink-0 ${shortsOn ? 'bg-orange-500' : 'bg-dark-600'} ${!userIsAdmin || !isGranted ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}>
                                <span className={`absolute top-[2px] h-[14px] w-[14px] rounded-full bg-white transition-transform shadow ${shortsOn ? 'left-[16px]' : 'left-[2px]'}`} />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {userIsAdmin && hasChanges && (
            <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-dark-900 border border-dark-500 rounded-xl px-6 py-3.5 shadow-2xl">
              <span className="text-sm text-dark-400">Unsaved changes</span>
              <button onClick={() => setPermissions({ ...originalPermissions })} className="px-4 py-2 bg-dark-700 text-white rounded-lg hover:bg-dark-600 text-sm">Reset</button>
              <button onClick={() => setConfirmDialog({ open: true })} disabled={saving} aria-busy={saving} data-testid="permissions-save" className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2 text-sm font-semibold">
                <BusyIcon busy={saving} icon={Save} /><span>{saving ? 'Saving…' : 'Save Changes'}</span>
              </button>
            </div>
          )}
        </div>
      )}

      {!selectedUserId && <div className="dashboard-card"><div className="text-center py-12"><Lock className="w-12 h-12 text-dark-500 mx-auto mb-4" /><p className="text-dark-300">Select a user to manage their index permissions</p></div></div>}

      {confirmDialog.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-dark-800 rounded-xl p-6 max-w-md w-full mx-4 border border-dark-600 shadow-xl">
            <div className="flex items-center gap-3 mb-4"><div className="w-12 h-12 rounded-full flex items-center justify-center bg-green-500/20"><Save className="w-6 h-6 text-green-400" /></div><div><h3 className="text-lg font-semibold text-white">Save Permissions?</h3><p className="text-dark-400 text-sm">This will update user access</p></div></div>
            {selectedUser && <div className="bg-dark-700/50 rounded-lg p-4 mb-4"><p className="text-white font-medium">{selectedUser.email || selectedUser.username}</p><p className="text-dark-400 text-sm mt-1">{Object.values(permissions).filter(Boolean).length} symbols will be accessible</p></div>}
            <div className="flex gap-3">
              <button onClick={() => setConfirmDialog({ open: false })} className="flex-1 px-4 py-2.5 bg-dark-700 text-white rounded-lg hover:bg-dark-600 font-medium">Cancel</button>
              <button onClick={confirmSave} className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center justify-center gap-2"><Check className="w-4 h-4" />Confirm</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Main Settings Page
// ============================================================================
const TAB_SLUGS = {
  'users': 'users',
  'permissions': 'permissions',
  'indexes': 'symbols',
};
const SLUG_FROM_ID = Object.fromEntries(Object.entries(TAB_SLUGS).map(([k, v]) => [v, k]));

const Settings = () => {
  const { tab } = useParams();
  const navigate = useNavigate();
  const activeTab = (tab && TAB_SLUGS[tab]) || 'users';
  const setActiveTab = (id) => navigate(`/settings/${SLUG_FROM_ID[id]}`, { replace: true });
  const userIsAdmin = isAdmin();

  useEffect(() => {
    if (!tab) navigate('/settings/users', { replace: true });
  }, [tab, navigate]);

  const tabs = [
    { id: 'users', label: 'User List', icon: UsersIcon },
    { id: 'permissions', label: 'Index Permissions', icon: Lock },
    { id: 'symbols', label: 'Index Management', icon: ToggleRight },
  ];

  return (
    <Layout>
      <div className="space-y-6">
        <div className="space-y-2">
          <div className="flex items-center space-x-2"><SettingsIcon className="w-8 h-8 text-blue-400" /><h1 className="text-3xl font-bold text-white">Settings</h1></div>
          <p className="text-dark-300">Manage users, permissions, and index configuration</p>
        </div>

        <div className="flex items-center space-x-1 bg-dark-800 p-1 rounded-lg w-fit">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-5 py-2.5 rounded-md text-sm font-medium transition-all ${activeTab === tab.id ? 'bg-primary-600 text-white shadow-lg' : 'text-dark-300 hover:text-white hover:bg-dark-700'}`}>
                <Icon className="w-4 h-4" /><span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {activeTab === 'users' && <UserListTab userIsAdmin={userIsAdmin} />}
        {activeTab === 'permissions' && <IndexPermissionsTab userIsAdmin={userIsAdmin} />}
        {activeTab === 'symbols' && <IndexManagementTab userIsAdmin={userIsAdmin} />}
      </div>
    </Layout>
  );
};

export default Settings;
